// src/firebase/firebase-config.js
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { post } from '../helpers/apiHelper';

// Configuración de Firebase (debe ser reemplazada con las credenciales reales)
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Verificar si la configuración está completa
const isFirebaseConfigured = () => {
    return firebaseConfig.apiKey &&
        firebaseConfig.authDomain &&
        firebaseConfig.projectId &&
        firebaseConfig.storageBucket &&
        firebaseConfig.messagingSenderId &&
        firebaseConfig.appId;
};

// Variables para Firebase
let app = null;
let messaging = null;

// Inicializar Firebase solo si está configurado
const initializeFirebase = () => {
    try {
        if (!isFirebaseConfigured()) {
            console.warn('Firebase no está configurado completamente. Algunas funcionalidades de notificación pueden no estar disponibles.');
            return false;
        }

        if (!app) {
            app = initializeApp(firebaseConfig);
            messaging = getMessaging(app);
        }
        return true;
    } catch (error) {
        console.error('Error inicializando Firebase:', error);
        return false;
    }
};

// Solicitar permisos de notificación
export const requestNotificationPermission = async () => {
    try {
        // Verificar si las notificaciones están soportadas
        if (!('Notification' in window)) {
            console.log('Las notificaciones no están soportadas en este navegador');
            return false;
        }

        const permission = await window.Notification.requestPermission();
        if (permission === 'granted') {
            console.log('Permisos de notificación concedidos');
            return true;
        } else {
            console.log('Permisos de notificación denegados');
            return false;
        }
    } catch (error) {
        console.error('Error solicitando permisos de notificación:', error);
        return false;
    }
};

// Obtener FCM token
export const getFCMToken = async (userId) => {
    try {
        // Verificar si Firebase está configurado
        if (!initializeFirebase()) {
            throw new Error('Firebase no está configurado');
        }

        const hasPermission = await requestNotificationPermission();
        if (!hasPermission) {
            throw new Error('Permisos de notificación no concedidos');
        }

        const token = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });

        if (token) {
            // Enviar token al backend
            await post('users/push-token', {
                userId,
                token,
                platform: 'web'
            });

            console.log('FCM Token obtenido y enviado al backend:', token);
            return token;
        } else {
            throw new Error('No se pudo obtener el token de FCM');
        }
    } catch (error) {
        console.error('Error obteniendo FCM token:', error);
        throw error;
    }
};

// Escuchar mensajes en primer plano
export const onMessageListener = () => {
    return new Promise((resolve) => {
        // Verificar si Firebase está configurado
        if (!initializeFirebase()) {
            console.warn('Firebase no está configurado. No se pueden recibir mensajes push.');
            resolve(null);
            return;
        }

        onMessage(messaging, (payload) => {
            console.log('Mensaje recibido en primer plano:', payload);

            // Mostrar notificación nativa
            if ('Notification' in window && window.Notification.permission === 'granted') {
                const notification = new window.Notification(payload.notification.title, {
                    body: payload.notification.body,
                    icon: payload.notification.icon || '/logo.png',
                    badge: '/logo.png',
                    tag: payload.data?.tag || 'default',
                    data: payload.data
                });

                // Manejar click en la notificación
                notification.onclick = () => {
                    window.focus();
                    notification.close();

                    // Navegar a la página correspondiente si se especifica
                    if (payload.data?.url) {
                        window.location.href = payload.data.url;
                    }
                };
            }

            resolve(payload);
        });
    });
};

// Verificar si las notificaciones están soportadas
export const isNotificationSupported = () => {
    try {
        return 'Notification' in window && 'serviceWorker' in navigator;
    } catch (error) {
        console.warn('Error verificando soporte de notificaciones:', error);
        return false;
    }
};

// Verificar si las notificaciones están habilitadas
export const isNotificationEnabled = () => {
    return 'Notification' in window && window.Notification.permission === 'granted';
};

// Verificar si Firebase está configurado
export const isFirebaseAvailable = () => {
    return isFirebaseConfigured();
};

// Obtener instancia de messaging (solo si está inicializado)
export const getMessagingInstance = () => {
    if (initializeFirebase()) {
        return messaging;
    }
    return null;
};

// Exportar configuración para uso en otros componentes
export const getFirebaseConfig = () => {
    return isFirebaseConfigured() ? firebaseConfig : null;
}; 